import { CONFIG } from '../../config.js';
import { LOT_TYPE } from './cityLayout.js';

/** @typedef {'A' | 'B' | 'C' | 'D' | 'E'} BuildingArchetypeId */

export const BUILDING_ARCHETYPE = {
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
  E: 'E',
};

/** @typedef {'posX' | 'negX' | 'posZ' | 'negZ'} StreetFace */

/**
 * @param {import('../../utils/Random.js').Random} rng
 * @param {number} lotType
 */
export function pickArchetype(rng, lotType) {
  if (lotType === LOT_TYPE.TOWER) return BUILDING_ARCHETYPE.D;
  if (lotType === LOT_TYPE.LARGE) return BUILDING_ARCHETYPE.E;
  if (lotType === LOT_TYPE.ALLEY) {
    return rng.next() < 0.55 ? BUILDING_ARCHETYPE.B : BUILDING_ARCHETYPE.C;
  }
  const roll = rng.next();
  if (roll < 0.16) return BUILDING_ARCHETYPE.A;
  if (roll < 0.42) return BUILDING_ARCHETYPE.B;
  if (roll < 0.58) return BUILDING_ARCHETYPE.C;
  if (roll < 0.74) return BUILDING_ARCHETYPE.D;
  return BUILDING_ARCHETYPE.E;
}

/**
 * @param {BuildingArchetypeId} id
 * @param {{ width: number; depth: number }} inner
 * @param {import('../../utils/Random.js').Random} rng
 * @param {{ cellsPerMeter: number; shape?: string }} detail
 */
export function resolveArchetypeFootprint(id, inner, rng, detail) {
  const cpm = detail.cellsPerMeter;
  /** @type {import('./DetailedBuilding.js').ArchetypeSpec} */
  const base = {
    shape: 'building',
    cellsPerMeter: cpm,
    sign: true,
    windows: true,
    entrance: true,
    rooftop: true,
    antenna: false,
    neon: false,
    silhouetteCap: false,
  };

  switch (id) {
    case BUILDING_ARCHETYPE.A:
      return {
        ...base,
        name: 'Skyscraper',
        width: rng.range(5.5, Math.min(9, inner.width * 0.55)),
        depth: rng.range(5.5, Math.min(9, inner.depth * 0.55)),
        height: rng.range(40, 62),
        shape: 'building',
        cellsPerMeter: cpm + 1,
        antenna: true,
        neon: rng.next() > 0.55,
      };
    case BUILDING_ARCHETYPE.B:
      return {
        ...base,
        name: 'Apartment',
        width: rng.range(6, inner.width * 0.62),
        depth: rng.range(6, inner.depth * 0.62),
        height: rng.range(12, 22),
        shape: 'building',
      };
    case BUILDING_ARCHETYPE.C:
      return {
        ...base,
        name: 'Industrial',
        width: rng.range(inner.width * 0.55, inner.width * 0.85),
        depth: rng.range(inner.depth * 0.5, inner.depth * 0.78),
        height: rng.range(8, 14),
        shape: 'box',
        cellsPerMeter: Math.max(4, cpm - 1),
        sign: rng.next() > 0.25,
        rooftop: rng.next() > 0.4,
      };
    case BUILDING_ARCHETYPE.D:
      return {
        ...base,
        name: 'NarrowTower',
        width: rng.range(2.6, 4.2),
        depth: rng.range(2.6, 4.2),
        height: rng.range(26, 44),
        shape: 'pillar',
        cellsPerMeter: cpm + 1,
        antenna: true,
        neon: true,
      };
    case BUILDING_ARCHETYPE.E:
      return {
        ...base,
        name: 'Corporate',
        width: rng.range(inner.width * 0.68, inner.width * 0.88),
        depth: rng.range(inner.depth * 0.68, inner.depth * 0.88),
        height: rng.range(18, 32),
        shape: 'building',
        cellsPerMeter: cpm,
        neon: true,
        antenna: rng.next() > 0.5,
      };
    default:
      return {
        ...base,
        name: 'Block',
        width: rng.range(5, inner.width * 0.5),
        depth: rng.range(5, inner.depth * 0.5),
        height: rng.range(8, 14),
      };
  }
}

/**
 * @param {ReturnType<typeof resolveArchetypeFootprint>} spec
 * @param {{ cellsPerMeter: number }} detail
 */
export function applyDistanceSilhouette(spec, detail) {
  const keepEntrance = CONFIG.interiors?.enabled;
  if (detail.cellsPerMeter >= 9) {
    return keepEntrance ? { ...spec, entrance: true } : spec;
  }
  if (detail.cellsPerMeter >= 6) {
    const out = {
      ...spec,
      height: spec.height * 1.08,
      silhouetteCap: spec.height > 20,
    };
    return keepEntrance ? { ...out, entrance: true } : out;
  }
  const out = {
    ...spec,
    height: spec.height * 1.18,
    silhouetteCap: true,
    windows: false,
    entrance: false,
    sign: spec.sign && spec.height > 16,
    cellsPerMeter: Math.max(3, spec.cellsPerMeter - 1),
  };
  return keepEntrance ? { ...out, entrance: true } : out;
}

/**
 * @param {number} centerX
 * @param {number} centerZ
 * @param {import('../../config.js').CONFIG['city']} cityConfig
 * @param {import('../../utils/Random.js').Random} rng
 * @returns {StreetFace}
 */
export function pickStreetFace(centerX, centerZ, cityConfig, rng) {
  const { blockCount, lotSize, roadWidth } = cityConfig;
  const offset = -(
    (roadWidth + blockCount * (lotSize + roadWidth)) /
    2
  );
  let bestDist = Infinity;
  /** @type {StreetFace} */
  let face = 'posX';

  for (let i = 0; i <= blockCount; i += 1) {
    const roadX =
      offset + i * (lotSize + roadWidth) + roadWidth / 2;
    const d = Math.abs(centerX - roadX);
    if (d < bestDist) {
      bestDist = d;
      face = centerX < roadX ? 'negX' : 'posX';
    }
  }
  for (let j = 0; j <= blockCount; j += 1) {
    const roadZ =
      offset + j * (lotSize + roadWidth) + roadWidth / 2;
    const d = Math.abs(centerZ - roadZ);
    if (d < bestDist) {
      bestDist = d;
      face = centerZ < roadZ ? 'negZ' : 'posZ';
    }
  }

  if (rng.next() < 0.12) {
    const alt = ['posX', 'negX', 'posZ', 'negZ'];
    face = /** @type {StreetFace} */ (alt[rng.int(0, alt.length - 1)]);
  }
  return face;
}
